import React, { useState, useMemo } from 'react';
import { message, Button, Modal, Select, Tag, Typography } from 'antd';
import styled from 'styled-components/macro';

import { EntityType, NodeRelationshipType, SearchResult, TermRelationshipType } from '../../../../types.generated';
import { useListCustomRelationTypesQuery } from '../../../../graphql/glossary.generated';
import { useAddRelatedTermsMutation } from '../../../../graphql/glossaryTerm.generated';
import { useAddTypeOfRelationsMutation } from '../../../../graphql/mutations.generated';
import { useAddRelatedNodesMutation } from '../../../../graphql/glossaryNode.generated';
import { useGetSearchResultsLazyQuery } from '../../../../graphql/search.generated';

import GlossaryBrowser from '../../../glossary/GlossaryBrowser/GlossaryBrowser';
import { BrowserWrapper } from '../../../shared/tags/AddTagsTermsModal';
import ClickOutside from '../../../shared/ClickOutside';
import TermLabel from '../../../shared/TermLabel';

import {
    ASSOCIATED_WITH,
    UTILIZED_BY,
    GLOSSARY_TERM,
    GLOSSARY_NODE,
    isCustomRelation,
    INVERSE_SUFFIX,
    parseSelectedKey,
} from '../utils';
import { RelatedTermTypes } from '../../shared/components/styled/search/types';
import { useEntityData, useRefetch } from '../../shared/EntityContext';
import { useTranslation } from '../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../useEntityRegistry';

const StyledSelect = styled(Select)`
    width: 480px;
`;

interface Props {
    onClose: () => void;
    entityType?: EntityType;
    isTypeTermGroup?: boolean;
    relationshipType?: TermRelationshipType;
    isAddingTermFromNode?: boolean;
}

const CORE_TERM_RELATIONS = [
    { label: RelatedTermTypes.hasRelatedTerms, value: TermRelationshipType.HasA }, // Contains
    { label: RelatedTermTypes.isRelatedTerms, value: TermRelationshipType.IsA }, // Inherits
];

const EXTENDED_TERM_RELATIONS = [
    { label: RelatedTermTypes.utilizedByTerms, value: UTILIZED_BY }, // Utilized by
    { label: RelatedTermTypes.associatedWithTerms, value: ASSOCIATED_WITH }, // Associated with
];

const TYPE_OF_RELATION = { label: RelatedTermTypes.typeOfRelations, value: RelatedTermTypes.typeOfRelations }; // Type Of
const HAS_TYPE_RELATION = { label: RelatedTermTypes.isTypeOf, value: RelatedTermTypes.isTypeOf }; // Has Type

function AddRelatedTermsModal(props: Props) {
    const { onClose, relationshipType, entityType, isTypeTermGroup, isAddingTermFromNode } = props;

    const [inputValue, setInputValue] = useState('');
    const [selectedUrns, setSelectedUrns] = useState<any[]>([]);
    const [selectedTerms, setSelectedTerms] = useState<any[]>([]);
    const [isFocusedOnInput, setIsFocusedOnInput] = useState(false);

    const getInitialRelationship = () => {
        if (isAddingTermFromNode) return RelatedTermTypes.isTypeOf;
        if (isTypeTermGroup) return RelatedTermTypes.typeOfRelations;
        return relationshipType;
    };

    const [relationship, setRelationship] = useState(getInitialRelationship());

    const entityRegistry = useEntityRegistry();
    const { urn: entityDataUrn } = useEntityData();
    const { t } = useTranslation();
    const refetch = useRefetch();

    const [AddRelatedTerms] = useAddRelatedTermsMutation();
    const [AddRelatedNodes] = useAddRelatedNodesMutation();
    const [AddTypeOfRelations] = useAddTypeOfRelationsMutation();

    // Fetch custom relation types
    const { data: customRelationTypesData } = useListCustomRelationTypesQuery();

    const isTypeOfByRelationship = relationship === RelatedTermTypes.typeOfRelations;
    const isAddingTermFromNodeAndIsTypeOf =
        isAddingTermFromNode && (isTypeOfByRelationship || relationship === RelatedTermTypes.isTypeOf);

    // Determine if we should use AddTypeOfRelations mutation
    const shouldUseAddTypeOfRelations = isTypeOfByRelationship || isAddingTermFromNodeAndIsTypeOf;

    /**
     * Determines if the current entity (where user is standing) is a GlossaryNode.
     * Used to determine target type when inverse swap happens.
     */
    const isCurrentEntityNode = isAddingTermFromNode || entityType === EntityType.GlossaryNode;

    /**
     * Executes the appropriate mutation with error handling and success feedback.
     * @param mutation - The Apollo mutation hook to execute
     * @param input - The input payload for the mutation
     */
    function executeMutation(mutation: any, input: any) {
        mutation({ variables: { input } })
            .catch((e) => {
                message.destroy();
                message.error({ content: `${t('Failed to add relation:')} ${e.message || ''}`, duration: 3 });
            })
            .finally(() => {
                message.loading({ content: t('Adding'), duration: 2 });
                setTimeout(() => {
                    message.success({ content: t('Added Successfully'), duration: 2 });
                    refetch();
                }, 2000);
            });
        onClose();
    }

    /**
     * Adds related terms/nodes based on the selected relationship type.
     *
     * MUTATION SELECTION LOGIC:
     * -------------------------
     * 1. AddTypeOfRelations - Used for "Type Of" / "Has Type" relationships
     * 2. AddRelatedNodes    - Used when targetUrns contains Node URNs
     * 3. AddRelatedTerms    - Used when targetUrns contains Term URNs
     *
     * CUSTOM RELATION HANDLING:
     * -------------------------
     * Custom relations can have an ':inverse' suffix which triggers source/target swap.
     * - Normal: currentEntity → selectedEntity
     * - Inverse: selectedEntity → currentEntity (source and target are swapped)
     *
     * API REQUIREMENTS:
     * -----------------
     * - addRelatedTerms: urn must be Term, termUrns must contain Terms only
     * - addRelatedNodes: urn can be Term or Node, nodeUrns must contain Nodes only
     */
    function addTerms() {
        // Prevent self-referencing relations
        if (selectedUrns.includes(entityDataUrn)) {
            message.error({ content: t('Cannot create relation with the same term.'), duration: 3 });
            return;
        }

        // Parse the relationship to extract custom relation info
        // - baseUrn: The custom relation type URN (without ':inverse' suffix)
        // - isInverseSelection: True if user selected the inverse direction
        const relationshipStr = relationship || '';
        const { baseUrn: customRelationUrn, isInverseSelection } = parseSelectedKey(relationshipStr);
        const isCustom = isCustomRelation(relationshipStr);

        // For inverse custom relations, swap source and target
        // Normal:  source = currentEntity, target = selectedEntity
        // Inverse: source = selectedEntity, target = currentEntity
        const shouldSwap = isCustom && isInverseSelection;
        const sourceUrn = shouldSwap ? selectedUrns[0] : entityDataUrn;
        const targetUrns = shouldSwap ? [entityDataUrn] : selectedUrns;

        // Handle "Type Of" / "Has Type" relations separately
        if (shouldUseAddTypeOfRelations) {
            const urn = isAddingTermFromNode ? selectedUrns[0] : entityDataUrn;
            const nodeUrns = isAddingTermFromNode ? [entityDataUrn] : selectedUrns;
            executeMutation(AddTypeOfRelations, { urn, nodeUrns });
            return;
        }

        // Determine if targetUrns contains Nodes or Terms to select correct mutation
        // - If swapped: targetUrns = [entityDataUrn] → check if current entity is Node
        // - If not swapped: targetUrns = selectedUrns → check if user is selecting Nodes (isTypeTermGroup)
        const isTargetNode = shouldSwap ? isCurrentEntityNode : isTypeTermGroup;

        if (isTargetNode) {
            // Target contains Nodes → use AddRelatedNodes mutation
            executeMutation(AddRelatedNodes, {
                urn: sourceUrn,
                nodeUrns: targetUrns,
                relationshipType: isCustom ? NodeRelationshipType.Custom : relationship,
                ...(isCustom && { customRelationTypeUrn: customRelationUrn }),
            });
        } else {
            // Target contains Terms → use AddRelatedTerms mutation
            executeMutation(AddRelatedTerms, {
                urn: sourceUrn,
                termUrns: targetUrns,
                relationshipType: isCustom ? TermRelationshipType.Custom : relationship,
                ...(isCustom && { customRelationTypeUrn: customRelationUrn }),
            });
        }
    }

    const [termSearch, { data: termSearchData }] = useGetSearchResultsLazyQuery();
    const termSearchResults = termSearchData?.search?.searchResults || [];

    const tagSearchOptions = termSearchResults.map((result: SearchResult) => {
        const displayName = entityRegistry.getDisplayName(result.entity.type, result.entity);

        return (
            <Select.Option value={result.entity.urn} key={result.entity.urn} name={displayName}>
                <TermLabel name={displayName} />
            </Select.Option>
        );
    });

    const handleSearch = (text: string) => {
        if (text.length > 0) {
            // Use GlossaryNode when adding term groups, GlossaryTerm otherwise
            const searchEntityType = isTypeTermGroup ? EntityType.GlossaryNode : EntityType.GlossaryTerm;

            termSearch({
                variables: {
                    input: {
                        type: searchEntityType,
                        query: text,
                        start: 0,
                        count: 20,
                    },
                },
            });
        }
    };

    // When a Tag or term search result is selected, add the urn to the Urns
    const onSelectValue = (urn: string) => {
        const newUrns = [...selectedUrns, urn];
        setSelectedUrns(newUrns);
        const selectedSearchOption = tagSearchOptions.find((option) => option.props.value === urn);
        setSelectedTerms([...selectedTerms, { urn, component: <TermLabel name={selectedSearchOption?.props.name} /> }]);
    };

    // When a Tag or term search result is deselected, remove the urn from the Owners
    const onDeselectValue = (urn: string) => {
        const newUrns = selectedUrns.filter((u) => u !== urn);
        setSelectedUrns(newUrns);
        setInputValue('');
        setIsFocusedOnInput(true);
        setSelectedTerms(selectedTerms.filter((term) => term.urn !== urn));
    };

    function selectTermFromBrowser(urn: string, displayName: string) {
        setIsFocusedOnInput(false);
        const newUrns = [...selectedUrns, urn];
        const uniqueUrns = Array.from(new Set(newUrns));
        setSelectedUrns(uniqueUrns);
        const uniqueTerms = selectedTerms.filter((term) => term.urn !== urn);
        setSelectedTerms([...uniqueTerms, { urn, component: <TermLabel name={displayName} /> }]);
    }

    function clearInput() {
        setInputValue('');
        setTimeout(() => setIsFocusedOnInput(true), 0); // call after click outside
    }

    function handleBlur() {
        setInputValue('');
    }

    const tagRender = (properties) => {
        // eslint-disable-next-line react/prop-types
        const { closable, onClose: close, value } = properties;
        const onPreventMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        const selectedItem = selectedTerms.find((term) => term.urn === value).component;

        return (
            <Tag onClose={close} closable={closable} className="base-tag" onMouseDown={onPreventMouseDown}>
                {selectedItem}
            </Tag>
        );
    };

    const getTitleName = () => {
        if (entityType === EntityType.GlossaryTerm) {
            return isTypeTermGroup ? t('Term Group') : t('Terms');
        }

        return t('Term Groups');
    };

    const getSelectEntity = () => {
        // If specifically adding term groups, only allow selecting nodes
        if (isTypeTermGroup) {
            return { selectNode: selectTermFromBrowser };
        }

        // If adding to a glossary term and relationship is Type Of, allow selecting nodes
        if (entityType === EntityType.GlossaryTerm && isTypeOfByRelationship) {
            return { selectNode: selectTermFromBrowser, selectTerm: selectTermFromBrowser };
        }

        // Default for glossary terms: only allow selecting terms
        if (entityType === EntityType.GlossaryTerm) {
            return { selectTerm: selectTermFromBrowser };
        }

        // For other entity types, allow nodes if Type Of relationship
        return isTypeOfByRelationship ? { selectNode: selectTermFromBrowser, hideTerms: true } : {};
    };
    const isShowingGlossaryBrowser = !inputValue && isFocusedOnInput;

    const selectEntity = getSelectEntity();

    const titleName = getTitleName();

    /**
     * Memoized relationship options for the dropdown.
     *
     * Custom Relation Strategy:
     * - Custom relations are defined with sourceEntityType → targetEntityType direction
     * - System only defines: Term→Term, Term→Node, Node→Node (no Node→Term)
     *
     * To show appropriate options:
     * 1. Direct relations: where source=currentEntity, target=whatWeAdding
     *    - Show 'name' (normal direction, no swap needed)
     *    - Show 'inverseName' with ':inverse' suffix (reverse direction, swap needed)
     *
     * 2. Reverse relations: where source=whatWeAdding, target=currentEntity
     *    - This handles cases like Node→Term (not explicitly defined)
     *    - We find Term→Node relations and show them from the inverse perspective
     *    - Show 'inverseName' with ':inverse' suffix
     *    - Show 'name' without suffix
     *
     * The ':inverse' suffix triggers source/target swap in buildInput()
     */
    const relationshipOptions = useMemo(() => {
        const customRelations = customRelationTypesData?.listCustomRelationTypes?.customRelations || [];

        // Determine current entity type (source perspective)
        const getSourceType = () => {
            if (isAddingTermFromNode) return GLOSSARY_NODE;
            return entityType === EntityType.GlossaryTerm ? GLOSSARY_TERM : GLOSSARY_NODE;
        };
        const sourceType = getSourceType();

        // Determine what we're adding (target perspective)
        const targetType = isTypeTermGroup ? GLOSSARY_NODE : GLOSSARY_TERM;

        const sameType = sourceType === targetType;

        // Get direct relations (source→target matches our perspective)
        const directRelations = customRelations.filter(
            (rel) => rel.sourceEntityType === sourceType && rel.targetEntityType === targetType,
        );

        // Get reverse relations (target→source, we'll show from inverse perspective)
        // This handles Node→Term by finding Term→Node and showing its inverse
        const reverseRelations = customRelations.filter(
            (rel) => rel.sourceEntityType === targetType && rel.targetEntityType === sourceType,
        );

        // Build options with deduplication
        const addedValues = new Set<string>();
        const customRelationOptions: Array<{ label: string; value: string }> = [];

        const addOption = (label: string, value: string) => {
            if (label && !addedValues.has(value)) {
                addedValues.add(value);
                customRelationOptions.push({ label, value });
            }
        };

        const processRelations = (relations, isDirect) => {
            relations.forEach((rel) => {
                if ((sameType || isDirect) && rel.name) {
                    addOption(rel.name, rel.urn);
                }
                if ((sameType || !isDirect) && rel.inverseName) {
                    addOption(rel.inverseName, `${rel.urn}${INVERSE_SUFFIX}`);
                }
            });
        };

        processRelations(directRelations, true);
        processRelations(reverseRelations, false);

        // Combine system relations with custom relations based on context
        if (isAddingTermFromNode) {
            return [HAS_TYPE_RELATION, ...CORE_TERM_RELATIONS, ...customRelationOptions];
        }

        if (isTypeTermGroup) {
            return [TYPE_OF_RELATION, ...customRelationOptions];
        }

        return [...CORE_TERM_RELATIONS, ...EXTENDED_TERM_RELATIONS, TYPE_OF_RELATION, ...customRelationOptions];
    }, [customRelationTypesData, entityType, isAddingTermFromNode, isTypeTermGroup]);

    return (
        <Modal
            className="f-modal"
            title={`${t('Add Related')} ${titleName}`}
            visible
            centered
            onCancel={onClose}
            footer={
                <>
                    <Button onClick={addTerms} disabled={!selectedUrns.length || !relationship} type="primary">
                        {t('Add')}
                    </Button>
                    <Button onClick={onClose} type="default">
                        {t('Cancel')}
                    </Button>
                </>
            }
        >
            <ClickOutside onClickOutside={() => setIsFocusedOnInput(false)}>
                <StyledSelect
                    autoFocus
                    showSearch
                    mode="multiple"
                    onBlur={handleBlur}
                    filterOption={false}
                    onClear={clearInput}
                    value={[]}
                    tagRender={tagRender}
                    onSearch={(value: string) => {
                        // eslint-disable-next-line react/prop-types
                        handleSearch(value.trim());
                        // eslint-disable-next-line react/prop-types
                        setInputValue(value.trim());
                    }}
                    defaultActiveFirstOption={false}
                    onFocus={() => setIsFocusedOnInput(true)}
                    placeholder={`${t('Search for Glossary')} ${titleName}...`}
                    onSelect={(asset: any) => onSelectValue(asset)}
                    onDeselect={(asset: any) => onDeselectValue(asset)}
                    dropdownStyle={isShowingGlossaryBrowser || !inputValue ? { display: 'none' } : {}}
                >
                    {tagSearchOptions}
                </StyledSelect>
                <BrowserWrapper isHidden={!isShowingGlossaryBrowser}>
                    <GlossaryBrowser isSelecting {...selectEntity} hideTerms={isTypeTermGroup} />
                </BrowserWrapper>
                <div className="f-tags-container mt-2">
                    {selectedTerms.map((term) => (
                        <Tag
                            closable
                            key={term.urn}
                            className="base-tag"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClose={() => onDeselectValue(term.urn)}
                        >
                            {term.component}
                        </Tag>
                    ))}
                </div>
                <div className="mt-4">
                    <Typography.Text className="f-text-gray-s100">{t('Select Term Relationship')}</Typography.Text>
                    <Select
                        value={relationship}
                        placeholder={t('Select Relationship Type')}
                        style={{ width: '100%', marginTop: 4 }}
                        onChange={setRelationship}
                        options={relationshipOptions}
                    />
                </div>
            </ClickOutside>
        </Modal>
    );
}

export default AddRelatedTermsModal;
